import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { PlusIcon } from '@heroicons/react/24/outline'
import { MainContent } from 'src/components/layout/main-content'
import { Button } from 'src/components/button'
import { Spinner } from 'src/components/spinner'
import { PaginationComponent } from 'src/components/pagination'
import { OrganizationStrip } from 'src/components/reservations/organization-strip'
import { useToast } from 'src/components/toast'
import { Switch } from 'src/components/switch'
import { ConfirmDeleteDialog } from 'src/components/confirm-delete-dialog'
import { TemplateCard } from 'src/components/whatsapp-templates/template-card'
import { CreateTemplateModal } from 'src/components/whatsapp-templates/create-template-modal'
import { EditTemplateModal } from 'src/components/whatsapp-templates/edit-template-modal'
import { RecommendedTemplateCard } from 'src/components/whatsapp-templates/recommended-template-card'
import { ItemCollection } from 'src/components/layout/item-collection'
import { ViewModeToggle } from 'src/components/view-mode-toggle'
import { useViewMode } from 'src/hooks/use-view-mode'
import { useAuth } from 'src/stores/auth'
import { usePermissions } from 'src/hooks/use-permissions'
import { useDeleteWithConfirmation } from 'src/hooks/use-delete-with-confirmation'
import { usePagination } from 'src/hooks/use-pagination'
import { whatsappTemplateApi } from 'src/services/whatsapp/api'
import { whatsappTemplateQueries } from 'src/services/whatsapp/queries'
import {
  RecommendedTemplate,
  recommendedTemplateShape,
  WHATSAPP_TEMPLATES_MANAGE_PERMISSION,
  WhatsappTemplate,
  templateStatusEnum,
} from 'src/services/whatsapp/models'
import type { OrganizationShape } from 'src/services/user/models'
import { extractErrorMessage } from 'src/utils/errors'

/**
 * The Templates tab of `/chat-clients` — moved here from the retired
 * `/whatsapp-templates` route so it can live behind the in-page tab toggle.
 * See specs/chat-templates-unified.
 */
export const TemplatesTab = () => {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const { orgsWithPermission } = usePermissions()
  const user = useAuth.use.user()

  // Only orgs where the user may manage templates — so the picker, and anything
  // they can author into, stays inside the permission boundary.
  const manageableMemberships = useMemo(() => {
    const allowed = new Set(orgsWithPermission(WHATSAPP_TEMPLATES_MANAGE_PERMISSION))
    return (user?.memberships ?? []).filter((m) => allowed.has(m.organizationId))
  }, [orgsWithPermission, user])

  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(
    () => manageableMemberships[0]?.organizationId ?? null,
  )
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [viewMode, setViewMode] = useViewMode('whatsappTemplates', 'card')
  const [includeDisabled, setIncludeDisabled] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<WhatsappTemplate | null>(null)
  // Set when duplicating a card — seeds the create modal, then clears on close
  // so a plain "New template" click afterwards opens a blank form again.
  const [duplicateSource, setDuplicateSource] = useState<WhatsappTemplate | null>(null)
  // Set when "Use template" is clicked on a recommended card; cleared on close.
  const [recommendedSource, setRecommendedSource] = useState<RecommendedTemplate | null>(null)
  const { pagination, paginationCallback, goToNextPage, goToPrevPage, goToPage, resetPagination } =
    usePagination()

  // A new org's template count has nothing to do with the previous org's page
  // position, so switching orgs always lands back on page 1.
  useEffect(() => {
    resetPagination()
  }, [selectedOrgId, resetPagination])

  // Map the manageable memberships onto the shape the shared card strip renders.
  // Only id and name are used for selection; template scoping needs nothing else.
  const organizations = useMemo<OrganizationShape[]>(
    () =>
      manageableMemberships.map((m) => ({
        id: m.organizationId,
        name: m.organizationName,
        defaultCurrency: '',
      })),
    [manageableMemberships],
  )

  const { data, isLoading } = useQuery({
    ...whatsappTemplateQueries.byOrg(
      selectedOrgId,
      pagination,
      paginationCallback,
      includeDisabled,
    ),
    // A freshly submitted template is `pending`; poll so its status flips to
    // approved/rejected as the backend approval sync runs, without a reload.
    refetchInterval: (query) =>
      query.state.data?.results?.some((tpl) => tpl.status === templateStatusEnum.PENDING)
        ? 15_000
        : false,
  })

  const templates = data?.results ?? []
  // Top-level list flag (tn-models only camelCases `results`, so it stays
  // snake_case): the org has a TRA/SIRE-reporting property.
  const hasReportingProperties = Boolean(
    (data as { has_reporting_properties?: boolean } | undefined)?.has_reporting_properties,
  )
  // Same snake_case top-level extra: templates offered to TRA/SIRE-reporting
  // orgs that the org hasn't created yet (server-filtered).
  const recommendedTemplates = useMemo(() => {
    const parsed = z
      .array(z.object(recommendedTemplateShape))
      .safeParse(
        (data as { recommended_templates?: unknown } | undefined)?.recommended_templates ?? [],
      )
    return parsed.success ? parsed.data : []
  }, [data])

  const { requestDelete, dialogProps: deleteDialogProps } = useDeleteWithConfirmation({
    mutationFn: whatsappTemplateApi.remove,
    queryKey: whatsappTemplateQueries.all(),
    invalidateOnSuccess: true,
    successMessage: t('whatsappTemplates.deleteSuccess'),
  })

  // The default template's delete dialog adds a reassurance line.
  const [isDeletingDefault, setIsDeletingDefault] = useState(false)

  // Disable is reversible and keeps the row, so it needs no confirmation dialog.
  const [disablingIds, setDisablingIds] = useState<Set<string>>(new Set())
  const { mutate: disableTemplate } = useMutation({
    mutationFn: (id: string) => whatsappTemplateApi.csc.disable(id),
    onMutate: (id: string) => setDisablingIds((prev) => new Set(prev).add(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappTemplateQueries.all() })
      toast({ description: t('whatsappTemplates.disableSuccess'), type: 'success' })
    },
    onError: () => {
      toast({ description: t('whatsappTemplates.disableError'), type: 'error' })
    },
    onSettled: (_data, _error, id) => {
      setDisablingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    },
  })

  const [enablingIds, setEnablingIds] = useState<Set<string>>(new Set())
  const { mutate: enableTemplate } = useMutation({
    mutationFn: (id: string) => whatsappTemplateApi.csc.enable(id),
    onMutate: (id: string) => setEnablingIds((prev) => new Set(prev).add(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappTemplateQueries.all() })
      toast({ description: t('whatsappTemplates.enableSuccess'), type: 'success' })
    },
    onError: () => {
      toast({ description: t('whatsappTemplates.enableError'), type: 'error' })
    },
    onSettled: (_data, _error, id) => {
      setEnablingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    },
  })

  // A card's Submit sends a draft to Meta. Field errors can't render inline on
  // a card, so the server's message is toasted; Edit shows them inline.
  const [submittingIds, setSubmittingIds] = useState<Set<string>>(new Set())
  const { mutate: submitTemplate } = useMutation({
    mutationFn: (id: string) => whatsappTemplateApi.csc.submit(id),
    onMutate: (id: string) => setSubmittingIds((prev) => new Set(prev).add(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: whatsappTemplateQueries.all() })
      toast({ description: t('whatsappTemplates.createSuccess'), type: 'success' })
    },
    onError: (error) => {
      toast({
        description: extractErrorMessage(error, t('whatsappTemplates.createError')),
        type: 'error',
      })
    },
    onSettled: (_data, _error, id) => {
      setSubmittingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    },
  })

  // Tracks which templates have an in-flight check so rapid repeat clicks on
  // the same card are blocked while leaving other cards' checks unaffected.
  const [checkingIds, setCheckingIds] = useState<Set<string>>(new Set())

  const { mutate: checkTemplateStatus } = useMutation({
    mutationFn: (id: string) => whatsappTemplateApi.csc.checkStatus(id),
    onMutate: (id: string) => {
      setCheckingIds((prev) => new Set(prev).add(id))
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: whatsappTemplateQueries.all() })
      toast({
        description: t(
          result.changed
            ? 'whatsappTemplates.checkStatusChanged'
            : 'whatsappTemplates.checkStatusStillPending',
        ),
        type: result.changed ? 'success' : 'default',
      })
    },
    onError: () => {
      toast({ description: t('whatsappTemplates.checkStatusError'), type: 'error' })
    },
    onSettled: (_data, _error, id) => {
      setCheckingIds((prev) => {
        const next = new Set(prev)
        next.delete(id)
        return next
      })
    },
  })

  return (
    <div className="flex-1 overflow-y-auto">
      <MainContent>
        {/* No titled ContentPane here: the chat shell's PageHeader already
            shows the "WhatsApp Templates" title above the tab toggle, so a
            second title inside would read as a page-within-a-page. Keep only
            the width-preserving wrapper ContentPane used to provide. */}
        <div className="flex min-w-0 flex-grow flex-col gap-6 @container">
          <div className="flex flex-wrap items-center justify-end gap-2">
            <ViewModeToggle value={viewMode} onChange={setViewMode} />
            {manageableMemberships.length > 0 && (
              <Button
                variant="primary"
                icon={<PlusIcon className="h-4 w-4" aria-hidden="true" />}
                onClick={() => setIsCreateOpen(true)}
                disabled={!selectedOrgId}
                data-testid="create-template-button"
              >
                {t('whatsappTemplates.newTemplate')}
              </Button>
            )}
          </div>

          {/* Same card-strip selector the chat page uses, so org scoping is
                explicit and visually consistent. A single-org admin sees one
                auto-selected card. Stacked above the grid (not a flex sibling
                of ContentPane) so it doesn't fight the grid for width. */}
          {organizations.length > 0 ? (
            <section data-testid="templates-org-selector">
              <h2 className="mb-2 text-sm font-semibold uppercase tracking-wide text-text-placeholder">
                {t('whatsappTemplates.orgLabel')}
              </h2>
              <OrganizationStrip
                organizations={organizations}
                selectedOrgId={selectedOrgId}
                onSelect={setSelectedOrgId}
                isLoading={false}
                testIdPrefix="templates-org-strip"
                renderBadge={() => null}
              />
            </section>
          ) : null}

          {recommendedTemplates.length > 0 ? (
            <section data-testid="recommended-templates">
              <h2 className="mb-1 text-sm font-semibold uppercase tracking-wide text-text-placeholder">
                {t('whatsappTemplates.recommendedTitle')}
              </h2>
              <p className="mb-3 text-sm text-text-secondary">
                {t('whatsappTemplates.recommendedHint')}
              </p>
              <ItemCollection viewMode={viewMode}>
                {recommendedTemplates.map((tpl) => (
                  <RecommendedTemplateCard
                    key={`${tpl.name}-${tpl.language}`}
                    template={tpl}
                    viewMode={viewMode}
                    onUse={(source) => {
                      setRecommendedSource(source)
                      setIsCreateOpen(true)
                    }}
                  />
                ))}
              </ItemCollection>
            </section>
          ) : null}

          <div>
            <p className="mb-4 text-sm text-text-secondary">{t('whatsappTemplates.subtitle')}</p>

            <label className="mb-4 flex items-center gap-2 text-sm text-text-secondary">
              <Switch
                checked={includeDisabled}
                onCheckedChange={(checked) => {
                  setIncludeDisabled(checked)
                  resetPagination()
                }}
                data-testid="include-disabled-toggle"
              />
              {t('whatsappTemplates.includeDisabled')}
            </label>

            {manageableMemberships.length === 0 ? (
              // A chat.access-only user has no org to scope templates to —
              // show a distinct no-access message rather than the
              // "no templates yet" copy, which would wrongly imply they
              // could create one (the button above is hidden for the same
              // reason).
              <div className="py-12 text-center" data-testid="templates-empty-state">
                <p className="text-text-placeholder">{t('whatsappTemplates.noManageAccess')}</p>
                <p className="mt-1 text-sm text-text-tertiary">
                  {t('whatsappTemplates.noManageAccessHint')}
                </p>
              </div>
            ) : isLoading ? (
              <div className="flex justify-center py-8">
                <Spinner size="lg" />
              </div>
            ) : templates.length === 0 ? (
              <div className="py-12 text-center" data-testid="templates-empty-state">
                <p className="text-text-placeholder">{t('whatsappTemplates.empty')}</p>
                <p className="mt-1 text-sm text-text-tertiary">
                  {t('whatsappTemplates.emptyHint')}
                </p>
              </div>
            ) : (
              <ItemCollection viewMode={viewMode} data-testid="templates-list">
                {templates.map((tpl: WhatsappTemplate) => (
                  <TemplateCard
                    key={tpl.id}
                    template={tpl}
                    viewMode={viewMode}
                    onDisable={disableTemplate}
                    isDisabling={disablingIds.has(tpl.id)}
                    onEnable={enableTemplate}
                    isEnabling={enablingIds.has(tpl.id)}
                    onDelete={() => {
                      setIsDeletingDefault(tpl.isDefault)
                      requestDelete(tpl.id, tpl.name)
                    }}
                    isDeleting={deleteDialogProps.isPending}
                    onEdit={setEditingTemplate}
                    onCheckStatus={checkTemplateStatus}
                    isCheckingStatus={checkingIds.has(tpl.id)}
                    onSubmit={submitTemplate}
                    isSubmitting={submittingIds.has(tpl.id)}
                    onDuplicate={(source) => {
                      setDuplicateSource(source)
                      setIsCreateOpen(true)
                    }}
                  />
                ))}
              </ItemCollection>
            )}

            {manageableMemberships.length > 0 && (
              <PaginationComponent
                pagination={pagination}
                isLoading={isLoading}
                onNextPage={goToNextPage}
                onPrevPage={goToPrevPage}
                onGoToPage={goToPage}
                className="mt-4"
              />
            )}
          </div>
        </div>
      </MainContent>

      {selectedOrgId ? (
        <CreateTemplateModal
          isOpen={isCreateOpen}
          onClose={() => {
            setIsCreateOpen(false)
            setDuplicateSource(null)
            setRecommendedSource(null)
          }}
          organizationId={selectedOrgId}
          sourceTemplate={duplicateSource}
          recommendedTemplate={recommendedSource}
          hasReportingProperties={hasReportingProperties}
        />
      ) : null}

      <EditTemplateModal
        template={editingTemplate}
        onClose={() => setEditingTemplate(null)}
        hasReportingProperties={hasReportingProperties}
      />

      <ConfirmDeleteDialog
        {...deleteDialogProps}
        detail={isDeletingDefault ? t('whatsappTemplates.deleteDefaultHint') : undefined}
      />
    </div>
  )
}
